import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Head } from "vite-react-ssg";
import NavigationButtons from "@/components/NavigationButtons";
import RelatedLinks from "@/components/RelatedLinks";

const canonical = "https://deepseek.ai/blog/deepseek-v4-pro-api-price-cut-permanent";
const title = "DeepSeek V4 Pro API: 75% Price Cut Made Permanent (2026)";
const description =
  "DeepSeek made its 75% V4 Pro API discount permanent — $0.435/1M input, $0.87/1M output. Now 3–19× cheaper than GPT-5.5, Gemini 3.1 Pro and Claude Opus 4.7.";

const html = `
<p><em>Disclaimer: deepseek.ai is an independent community and fan site, not officially affiliated with DeepSeek AI. Rates reflect DeepSeek's first-party API pricing as reported by <a href="https://artificialanalysis.ai" target="_blank" rel="noopener noreferrer">Artificial Analysis</a> and may change. See our <a href="/pricing">pricing page</a> for the current live rates and the newer surge-pricing model.</em></p>

<p><strong>Announced 23 May 2026:</strong> <strong>DeepSeek has made its temporary 75% price cut on the first-party V4 Pro API permanent.</strong> The move puts <a href="/deepseek-v4">V4 Pro</a> firmly on the Pareto frontier of intelligence-vs-cost, alongside <a href="/deepseek-v4-flash-review">V4 Flash</a>.</p>

<h2>The New V4 Pro Pricing</h2>
<table style="width:100%;border-collapse:collapse;margin:1.5rem 0;">
  <thead>
    <tr style="background:#f3f4f6;text-align:left;">
      <th style="padding:0.75rem;border:1px solid #e5e7eb;">Token Type</th>
      <th style="padding:0.75rem;border:1px solid #e5e7eb;">Previous / 1M</th>
      <th style="padding:0.75rem;border:1px solid #e5e7eb;">New / 1M</th>
      <th style="padding:0.75rem;border:1px solid #e5e7eb;">Change</th>
    </tr>
  </thead>
  <tbody>
    <tr><td style="padding:0.75rem;border:1px solid #e5e7eb;">Input</td><td style="padding:0.75rem;border:1px solid #e5e7eb;">$1.74</td><td style="padding:0.75rem;border:1px solid #e5e7eb;"><strong>$0.435</strong></td><td style="padding:0.75rem;border:1px solid #e5e7eb;">−75%</td></tr>
    <tr><td style="padding:0.75rem;border:1px solid #e5e7eb;">Output</td><td style="padding:0.75rem;border:1px solid #e5e7eb;">$3.48</td><td style="padding:0.75rem;border:1px solid #e5e7eb;"><strong>$0.87</strong></td><td style="padding:0.75rem;border:1px solid #e5e7eb;">−75%</td></tr>
    <tr><td style="padding:0.75rem;border:1px solid #e5e7eb;">Cached input</td><td style="padding:0.75rem;border:1px solid #e5e7eb;">—</td><td style="padding:0.75rem;border:1px solid #e5e7eb;"><strong>$0.0036</strong></td><td style="padding:0.75rem;border:1px solid #e5e7eb;">~99.8% off input</td></tr>
  </tbody>
</table>
<p>Using Artificial Analysis's standard <strong>7:2:1 blended pricing</strong>, V4 Pro lands at roughly <strong>$0.18 per 1M blended tokens</strong>.</p>

<h2>Running the Intelligence Index: What It Costs</h2>
<ul>
  <li><strong>DeepSeek V4 Pro:</strong> ~$268</li>
  <li><strong>Gemini 3.1 Pro Preview:</strong> ~$892 — V4 Pro is <strong>~3× cheaper</strong></li>
  <li><strong>GPT-5.5 (xhigh):</strong> ~$3,357 — V4 Pro is <strong>~12× cheaper</strong></li>
  <li><strong>Claude Opus 4.7 (max):</strong> ~$5,117 — V4 Pro is <strong>~19× cheaper</strong></li>
</ul>
<p>V4 Pro sits in the upper-right "most attractive quadrant" of the intelligence-vs-cost chart while costing a fraction to run.</p>

<h2>Why DeepSeek Can Do This</h2>
<ol>
  <li><strong>MoE efficiency.</strong> V4's sparse <a href="/blog/deepseek-v4-unveiled-1-6-trillion-parameters">1.6T-parameter MoE architecture</a> activates only a small slice of the network per token; inference cost scales with active parameters, not total parameters.</li>
  <li><strong>Compressed attention.</strong> The <a href="/blog/deepseek-v4-compressed-attention">compressed-attention mechanism</a> cuts KV-cache pressure — the dominant cost of long-context inference.</li>
  <li><strong>Aggressive prefix caching.</strong> The $0.0036/1M cached-input rate (a ~99.8% discount vs. fresh input) makes agentic workflows and long system prompts almost free on the input side. This is the lever pulling blended pricing down to ~$0.18.</li>
</ol>
<p>Combined with DeepSeek's <a href="/blog/deepseek-agi-10-billion-funding-round">recently-closed ~$7.4B funding round</a>, the lab has both the capital runway and architectural edge to sustain frontier-tier pricing pressure.</p>

<h2>What This Means for Builders</h2>
<ul>
  <li><strong>Agentic systems:</strong> Multi-step agents that cost $5–20 per task on Claude Opus or GPT-5.5 can run on V4 Pro for cents.</li>
  <li><strong>Long-context RAG:</strong> Prefix caching at $0.0036/1M makes 100K+ token system prompts economically viable.</li>
  <li><strong>Reasoning at scale:</strong> Workloads that used to fall back to <a href="/blog/deepseek-r2-ai-model-launch-2025">R-series</a> for cost reasons no longer have to compromise.</li>
  <li><strong>Open-weight fallback:</strong> Because V4 is open-weight, you can self-host the same architecture if first-party pricing ever changes.</li>
</ul>

<h2>Note on Surge Pricing</h2>
<p>DeepSeek has since layered a <a href="/blog/deepseek-v4-ga-surge-pricing-migration">peak-hour surge premium</a> on top of these permanent base rates and retired the legacy <a href="/blog/deepseek-chat-reasoner-retired-billing-impact">deepseek-chat / deepseek-reasoner endpoints</a>. Off-peak base rates below still apply; peak-hour multipliers now apply on top.</p>

<h2>Bottom Line</h2>
<p>Making the 75% cut permanent reframes the entire frontier-AI cost curve. At ~$0.18 blended per 1M tokens, V4 Pro is meaningfully redefining what frontier-tier reasoning costs to operate. Western labs face a choice: match the pricing, differentiate on capability, or watch high-volume workloads migrate.</p>
`;

const schemaData = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: title,
  datePublished: "2026-05-23T07:26:25+00:00",
  dateModified: "2026-07-25T00:00:00+00:00",
  author: { "@type": "Organization", name: "The Independent DeepSeek Guide", url: "https://deepseek.ai" },
  publisher: {
    "@type": "Organization",
    name: "The Independent DeepSeek Guide",
    logo: { "@type": "ImageObject", url: "https://deepseek.ai/logo.png" },
  },
  description,
  mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
};

const DeepseekV4ProPriceCutPermanent = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Head>
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate("/blog")} className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blog
        </Button>
        <h1 className="text-4xl md:text-5xl font-bold mb-6">
          DeepSeek V4 Pro API: 75% Price Cut Made Permanent
        </h1>
        <p className="text-muted-foreground mb-8">Announced 23 May 2026 · Reviewed 25 July 2026</p>
        <article
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: html }}
        />
        <NavigationButtons />
        <RelatedLinks />
      </main>
    </div>
  );
};

export default DeepseekV4ProPriceCutPermanent;
